import { useEffect, useState } from 'react'
import { Button, Card, DataTable } from '@w7/ui'
import { bookmakerApi, bmErrorMessage, unwrapBmList } from '@/api/bookmaker'
import { BmPage } from './BmPage'

export function RatesDiffPage() {
  const [rows, setRows] = useState<Record<string, unknown>[]>([])
  const [runNo, setRunNo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [msg, setMsg] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  async function load() {
    setBusy(true)
    setError(null)
    try {
      const data = await bookmakerApi.ratesDiffGetAll()
      setRows(unwrapBmList(data))
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function onAdd() {
    const n = Number(runNo)
    if (!Number.isFinite(n) || n < 0.01 || n > 999) {
      setError('Enter valid rate between 0.01 to 999')
      return
    }
    setBusy(true)
    setError(null)
    setMsg(null)
    try {
      await bookmakerApi.ratesDiffSave({ runNo: n })
      setRunNo('')
      setMsg('Rate diff saved successfully')
      await load()
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  async function onDelete(row: Record<string, unknown>) {
    const id = String(row._id ?? row.id ?? '')
    if (!id) return
    setBusy(true)
    setError(null)
    setMsg(null)
    try {
      await bookmakerApi.ratesDiffDelete({ _id: id })
      setMsg('Deleted successfully')
      await load()
    } catch (e) {
      setError(bmErrorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <BmPage
      title="Rates Diff"
      description="Preset rate-diff buttons for Markets View (stored in bookmaker.ratesdiff)"
      error={error}
      actions={
        <>
          <Button onClick={() => void load()} disabled={busy}>
            Refresh
          </Button>
        </>
      }
    >
      {msg ? <p style={{ margin: 0, opacity: 0.8 }}>{msg}</p> : null}
      <Card>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'end', marginBottom: 16 }}>
          <label style={{ display: 'grid', gap: 4 }}>
            <span style={{ fontSize: 13, opacity: 0.8 }}>Run No (0.01–999)</span>
            <input
              type="number"
              min={0.01}
              max={999}
              step="0.01"
              value={runNo}
              disabled={busy}
              onChange={(e) => setRunNo(e.target.value)}
              style={{ padding: '6px 10px', minWidth: 140 }}
            />
          </label>
          <Button disabled={busy} onClick={() => void onAdd()}>
            Add
          </Button>
        </div>
        <DataTable
          label="Rates Diff"
          columns={[
            {
              id: 'runNo',
              header: 'Run No',
              cell: (r: Record<string, unknown>) =>
                String(r.runNo ?? r.rateDiff ?? r.value ?? '—'),
            },
            {
              id: 'isActive',
              header: 'Active',
              cell: (r: Record<string, unknown>) => String(r.isActive ?? false),
            },
            {
              id: 'actions',
              header: '',
              cell: (r: Record<string, unknown>) => (
                <Button disabled={busy} onClick={() => void onDelete(r)}>
                  Delete
                </Button>
              ),
            },
          ]}
          rows={rows}
          loading={busy}
          rowKey={(r) => String(r._id ?? r.id ?? r.runNo ?? Math.random())}
        />
      </Card>
    </BmPage>
  )
}
